// Serviço para gerenciar configurações da plataforma no Supabase
import { supabase } from '../lib/supabase';

export interface Configuracao {
  id: string;
  chave: string;
  valor: string;
  tipo: string;
  descricao: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConfiguracaoDB {
  id: string;
  chave: string;
  valor: string | null;
  tipo: string;
  descricao: string | null;
  created_at: string;
  updated_at: string;
}

function configuracaoDBToConfiguracao(configDB: ConfiguracaoDB): Configuracao {
  return {
    id: configDB.id,
    chave: configDB.chave,
    valor: configDB.valor || '',
    tipo: configDB.tipo,
    descricao: configDB.descricao || null,
    createdAt: configDB.created_at,
    updatedAt: configDB.updated_at,
  };
}

// Buscar todas as configurações
export async function getConfiguracoes(): Promise<Configuracao[]> {
  try {
    const { data, error } = await (supabase as any)
      .from('configuracoes_plataforma')
      .select('*')
      .order('chave', { ascending: true });

    if (error) throw error;

    return (data || []).map(configuracaoDBToConfiguracao);
  } catch (error) {
    console.error('[configuracoesService] Erro ao buscar configurações:', error);
    return [];
  }
}

// Buscar configuração por chave
export async function getConfiguracaoPorChave(chave: string): Promise<Configuracao | null> {
  try {
    const { data, error } = await (supabase as any)
      .from('configuracoes_plataforma')
      .select('*')
      .eq('chave', chave)
      .single();

    if (error) throw error;
    if (!data) return null;

    return configuracaoDBToConfiguracao(data);
  } catch (error) {
    console.error('[configuracoesService] Erro ao buscar configuração:', error);
    return null;
  }
}

// Buscar configurações como objeto
export async function getConfiguracoesAsObject(): Promise<Record<string, any>> {
  try {
    const configuracoes = await getConfiguracoes();
    const result: Record<string, any> = {};

    configuracoes.forEach((config) => {
      let value: any = config.valor;
      
      // Converter valores baseado no tipo
      if (config.tipo === 'boolean') {
        value = config.valor === 'true';
      } else if (config.tipo === 'number') {
        value = Number(config.valor);
      } else if (config.tipo === 'json') {
        try {
          value = JSON.parse(config.valor);
        } catch {
          value = config.valor;
        }
      }

      result[config.chave] = value;
    });

    return result;
  } catch (error) {
    console.error('[configuracoesService] Erro ao buscar configurações como objeto:', error);
    return {};
  }
}

// Criar ou atualizar configuração
export async function upsertConfiguracao(chave: string, valor: string, tipo: string = 'string', descricao: string | null = null): Promise<Configuracao | null> {
  try {
    const configDB = {
      chave,
      valor,
      tipo,
      descricao,
    };

    const { data, error } = await (supabase as any)
      .from('configuracoes_plataforma')
      .upsert(configDB, { onConflict: 'chave' })
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;

    return configuracaoDBToConfiguracao(data);
  } catch (error) {
    console.error('[configuracoesService] Erro ao criar/atualizar configuração:', error);
    throw error;
  }
}

// Atualizar múltiplas configurações
export async function updateConfiguracoes(configuracoes: Record<string, any>): Promise<boolean> {
  try {
    const promises = Object.entries(configuracoes).map(([chave, valor]) => {
      let tipo = 'string';
      if (typeof valor === 'boolean') tipo = 'boolean';
      else if (typeof valor === 'number') tipo = 'number';
      else if (typeof value === 'object') {
        tipo = 'json';
        valor = JSON.stringify(valor);
      }

      return upsertConfiguracao(chave, String(valor), tipo);
    });

    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('[configuracoesService] Erro ao atualizar configurações:', error);
    throw error;
  }
}

// Deletar configuração
export async function deleteConfiguracao(chave: string): Promise<boolean> {
  try {
    const { error } = await (supabase as any)
      .from('configuracoes_plataforma')
      .delete()
      .eq('chave', chave);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('[configuracoesService] Erro ao deletar configuração:', error);
    throw error;
  }
}

